import React from 'react';
import { Button, Card, Row, Col, Typography, Space, Divider, Collapse } from 'antd';
import {
    GiftOutlined,
    SafetyOutlined,
    MailOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    HeartOutlined,
    CheckCircleOutlined,
    RocketOutlined,
    ClockCircleOutlined,
    LockOutlined,
    DollarOutlined,
    QuestionCircleOutlined,
    CoffeeOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const Landing: React.FC = () => {
    const { loginWithRedirect } = useAuth0();
    const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const trustBadges = [
        { icon: <DollarOutlined />, text: '100% Gratis' },
        { icon: <LockOutlined />, text: 'Privacy Garantita' },
        { icon: <ClockCircleOutlined />, text: 'Pronto in 2 minuti' },
        { icon: <CheckCircleOutlined />, text: 'Zero Pubblicità' }
    ];

    const features = [
        {
            icon: <ThunderboltOutlined style={{ fontSize: '56px', color: '#f57c00' }} />,
            title: 'Veloce e Semplice',
            description: 'Crea il tuo sorteggio in pochi click. Aggiungi i partecipanti, imposta le esclusioni e il gioco è fatto!',
            example: 'tutto in 5 minuti!'
        },
        {
            icon: <SafetyOutlined style={{ fontSize: '56px', color: '#2e7d32' }} />,
            title: 'Sicuro e Privato',
            description: 'Ogni partecipante riceverà solo il nome della persona a cui fare il regalo. Nessuna spiata!',
            example: 'La sorpresa è garantita al 100%'
        },
        {
            icon: <MailOutlined style={{ fontSize: '56px', color: '#0288d1' }} />,
            title: 'Notifiche Email',
            description: 'Invia automaticamente le email a tutti i partecipanti con il loro match segreto.',
            example: 'Email personalizzate per ogni partecipante'
        },
        {
            icon: <TeamOutlined style={{ fontSize: '56px', color: '#7b1fa2' }} />,
            title: 'Gestione Esclusioni',
            description: 'Imposta facilmente chi non può ricevere regali da chi. Perfetto per coppie e famiglie!',
            example: 'Evita che le coppie si scambino tra loro'
        }
    ];

    const steps = [
        {
            number: '1',
            title: 'Crea il Sorteggio',
            description: 'Dai un nome al tuo evento e aggiungi tutti i partecipanti o condividi con loro via WhatsApp il link per iscriversi.'
        },
        {
            number: '2',
            title: 'Imposta le Regole',
            description: 'Definisci le esclusioni: chi non può ricevere regali da chi (es. coppie).'
        },
        {
            number: '3',
            title: 'Sorteggia!',
            description: 'Premi il pulsante magico e lascia che l\'algoritmo crei le coppie perfette.'
        },
        {
            number: '4',
            title: 'Invia le Email',
            description: 'Ogni partecipante riceverà un\'email con il nome del suo match segreto.'
        },
        {
            number: '5',
            title: 'Email persa?',
            description: 'Nessun problema! Genera un link segreto personalizzato per ogni partecipante.'
        }
    ];

    const faqs = [
        {
            question: 'È davvero completamente gratis?',
            answer: 'Sì! Babbo Natale Segreto è 100% gratuito, senza costi nascosti, senza limiti di utilizzo e senza pubblicità.\nL\'ho creato per risolvere un mio problema e per imparare a costruire l\'app, quindi rimarrà sempre gratis. Puoi creare tutti i sorteggi che vuoi!'
        },
        /*{
            question: 'I miei dati sono al sicuro?',
            answer: 'Assolutamente sì. Utilizziamo Auth0 per l\'autenticazione, uno dei sistemi più sicuri al mondo. I tuoi dati sono protetti e non vengono mai condivisi con terze parti. Solo tu come admin puoi vedere l\'intero sorteggio.'
        },*/
        {
            question: 'Posso modificare il sorteggio dopo averlo creato?',
            answer: 'Sì! Puoi aggiungere o rimuovere partecipanti, modificare le esclusioni e rigenerare il sorteggio prima di inviare le email. Una volta inviate le email, il sorteggio viene bloccato per mantenere il segreto.'
        },
        {
            question: 'Cosa succede se qualcuno perde l\'email?',
            answer: 'Nessun problema! Come amministratore puoi generare un link segreto personalizzato per ogni partecipante. Basta inviare il link e potranno scoprire il loro match.'
        },
        {
            question: 'Come funzionano le esclusioni?',
            answer: 'Le esclusioni ti permettono di impedire che determinate persone ricevano regali da altre. È perfetto per evitare che le coppie si scambino tra loro o che i membri della stessa famiglia si peschino.'
        },
        {
            question: 'Quante persone possono partecipare?',
            answer: 'Non c\'è un limite! Puoi organizzare sorteggi con 3 persone o con 100+. Il nostro algoritmo gestisce anche sorteggi complessi con molte esclusioni.'
        }
    ];

    return (
        <div style={{
            background: '#ffffff',
            minHeight: '100vh',
            overflowX: 'hidden'
        }}>
            {/* Hero Section - Migliorato */}
            <div style={{
                background: '#ffffff',
                padding: isMobile ? '60px 20px 40px' : '100px 40px 60px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                borderTop: '8px solid #d32f2f',
                borderBottom: '4px solid #d32f2f',
                boxShadow: 'inset 0 0 0 4px #ffffff, inset 0 0 0 8px #c62828'
            }}>
                {/* Candy cane stripes decoration */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100px',
                    width: '200px',
                    height: '100%',
                    background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(211,47,47,0.05) 10px, rgba(211,47,47,0.05) 20px)',
                    transform: 'skewX(-10deg)'
                }} />
                <div style={{
                    position: 'absolute',
                    top: 0,
                    right: '-100px',
                    width: '200px',
                    height: '100%',
                    background: 'repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(211,47,47,0.05) 10px, rgba(211,47,47,0.05) 20px)',
                    transform: 'skewX(10deg)'
                }} />

                <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
                    <div style={{
                        display: 'inline-block',
                        padding: '20px',
                        borderRadius: '50%',
                        border: '4px solid #d32f2f',
                        background: 'white',
                        marginBottom: '24px',
                        boxShadow: '0 4px 20px rgba(211,47,47,0.2)'
                    }}>
                        <GiftOutlined style={{
                            fontSize: isMobile ? '72px' : '96px',
                            color: '#d32f2f',
                            animation: 'float 3s ease-in-out infinite'
                        }} />
                    </div>
                    <Title
                        level={1}
                        style={{
                            color: '#d32f2f',
                            fontSize: isMobile ? '36px' : '56px',
                            fontWeight: 700,
                            marginBottom: '16px',
                            textShadow: 'none'
                        }}
                    >
                        Babbo Natale Segreto
                    </Title>

                    {/* Subtitle più diretto */}
                    <Title
                        level={3}
                        style={{
                            color: '#333',
                            fontSize: isMobile ? '20px' : '28px',
                            fontWeight: 600,
                            marginBottom: '16px',
                            marginTop: 0
                        }}
                    >
                        Organizza il tuo Secret Santa in 2 minuti. Zero pensieri, zero imbarazzi.
                    </Title>

                    <Paragraph
                        style={{
                            color: '#666',
                            fontSize: isMobile ? '16px' : '18px',
                            marginBottom: '40px',
                            maxWidth: '700px',
                            margin: '0 auto 40px',
                            lineHeight: '1.6'
                        }}
                    >
                        Sorteggi automatici, email personalizzate e gestione completa delle esclusioni.
                        Rendi il tuo scambio regali perfetto senza stress.
                    </Paragraph>

                    <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={() => loginWithRedirect()}
                        style={{
                            height: '64px',
                            fontSize: '20px',
                            fontWeight: 600,
                            borderRadius: '32px',
                            padding: '0 48px',
                            background: '#d32f2f',
                            color: 'white',
                            border: '3px solid #b71c1c',
                            boxShadow: '0 8px 24px rgba(211,47,47,0.4)',
                            minWidth: isMobile ? '280px' : 'auto',
                            marginBottom: '24px'
                        }}
                    >
                        Crea il Tuo Sorteggio Ora
                    </Button>

                    <div>
                        <Text style={{ color: '#888', fontSize: '14px' }}>
                            ⏱️ Bastano 2 minuti
                        </Text>
                    </div>
                </div>
            </div>

            {/* Trust Badges Section */}
            <div style={{
                background: '#f8f9fa',
                padding: isMobile ? '30px 20px' : '40px 40px',
                borderBottom: '2px solid #e0e0e0'
            }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <Row gutter={[16, 16]} justify="center">
                        {trustBadges.map((badge, index) => (
                            <Col key={index} xs={12} sm={6}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '32px', color: '#2e7d32', marginBottom: '8px' }}>
                                        {badge.icon}
                                    </div>
                                    <Text style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                                        {badge.text}
                                    </Text>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* What is Secret Santa Section - Spostato più in alto e compatto */}
            <div style={{
                padding: isMobile ? '60px 20px' : '80px 40px',
                background: '#fff',
                maxWidth: '900px',
                margin: '0 auto'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <HeartOutlined style={{ fontSize: '48px', color: '#d32f2f', marginBottom: '16px' }} />
                    <Title level={2} style={{ marginBottom: '16px', fontSize: isMobile ? '28px' : '36px' }}>
                        Cos'è il Secret Santa?
                    </Title>
                    <Paragraph style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', maxWidth: '700px', margin: '0 auto 32px' }}>
                        Il Secret Santa (Babbo Natale Segreto) è un gioco di scambio regali dove ogni persona riceve
                        segretamente il nome di qualcuno a cui fare un regalo, senza che nessun altro lo sappia.
                        È perfetto per Natale in famiglia, con amici o colleghi!
                    </Paragraph>
                    <Row gutter={[24, 16]} justify="center">
                        <Col xs={24} sm={8}>
                            <CheckCircleOutlined style={{ fontSize: '24px', color: '#2e7d32', marginBottom: '8px' }} />
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Divertente</div>
                            <Text style={{ color: '#666', fontSize: '14px' }}>Il mistero rende tutto più emozionante!</Text>
                        </Col>
                        <Col xs={24} sm={8}>
                            <CheckCircleOutlined style={{ fontSize: '24px', color: '#2e7d32', marginBottom: '8px' }} />
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Equo</div>
                            <Text style={{ color: '#666', fontSize: '14px' }}>Tutti fanno e ricevono un regalo</Text>
                        </Col>
                        <Col xs={24} sm={8}>
                            <CheckCircleOutlined style={{ fontSize: '24px', color: '#2e7d32', marginBottom: '8px' }} />
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Economico</div>
                            <Text style={{ color: '#666', fontSize: '14px' }}>Un solo regalo a testa!</Text>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Features Section - Migliorata con esempi */}
            <div style={{
                padding: isMobile ? '60px 20px' : '80px 40px',
                maxWidth: '1200px',
                margin: '0 auto',
                background: '#f8f9fa'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Title level={2} style={{ fontSize: isMobile ? '28px' : '40px', marginBottom: '16px' }}>
                        Tutto quello che ti serve
                    </Title>
                    <Paragraph style={{ fontSize: '18px', color: '#666' }}>
                        Strumenti potenti per organizzare lo scambio regali perfetto
                    </Paragraph>
                </div>

                <Row gutter={[32, 32]}>
                    {features.map((feature, index) => (
                        <Col key={index} xs={24} sm={12} lg={6}>
                            <Card
                                hoverable
                                style={{
                                    height: '100%',
                                    borderRadius: '20px',
                                    border: '3px solid #d32f2f',
                                    boxShadow: '0 4px 20px rgba(211,47,47,0.15)',
                                    textAlign: 'center',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    background: 'white',
                                    cursor: 'pointer'
                                }}
                                styles={{ body: { padding: '40px 24px' } }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(211,47,47,0.25)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(211,47,47,0.15)';
                                }}
                            >
                                <div style={{ marginBottom: '20px', transition: 'transform 0.3s ease' }}>
                                    {feature.icon}
                                </div>
                                <Title level={4} style={{ marginBottom: '12px', fontSize: '18px' }}>
                                    {feature.title}
                                </Title>
                                <Paragraph style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                                    {feature.description}
                                </Paragraph>
                                <div style={{
                                    background: '#f8f9fa',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    marginTop: '16px'
                                }}>
                                    <Text style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 500 }}>
                                        💡 {feature.example}
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* CTA dopo Features */}
                <div style={{ textAlign: 'center', marginTop: '60px' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<GiftOutlined />}
                        onClick={() => loginWithRedirect()}
                        style={{
                            height: '56px',
                            fontSize: '18px',
                            fontWeight: 600,
                            borderRadius: '28px',
                            padding: '0 40px',
                            background: '#2e7d32',
                            color: 'white',
                            border: '3px solid #1b5e20',
                            boxShadow: '0 8px 24px rgba(46,125,50,0.3)',
                            minWidth: isMobile ? '280px' : 'auto'
                        }}
                    >
                        Inizia Subito - È Gratis!
                    </Button>
                </div>
            </div>

            {/* How It Works Section - Layout migliorato per 5 step */}
            <div style={{
                background: '#ffffff',
                padding: isMobile ? '60px 20px' : '80px 40px',
                borderTop: '2px solid #d32f2f',
                borderBottom: '2px solid #d32f2f'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <Title level={2} style={{ fontSize: isMobile ? '28px' : '40px', marginBottom: '16px' }}>
                            Come funziona?
                        </Title>
                        <Paragraph style={{ fontSize: '18px', color: '#666' }}>
                            5 semplici passaggi per il tuo Secret Santa perfetto
                        </Paragraph>
                    </div>

                    {/* Prima riga con 3 step */}
                    <Row gutter={[32, 32]} style={{ marginBottom: isMobile ? '0' : '32px' }}>
                        {steps.slice(0, 3).map((step, index) => (
                            <Col key={index} xs={24} sm={12} lg={8}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        background: 'white',
                                        border: '4px solid #d32f2f',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 24px',
                                        boxShadow: '0 4px 20px rgba(211,47,47,0.2)'
                                    }}>
                                        <Text style={{
                                            fontSize: '36px',
                                            fontWeight: 'bold',
                                            color: '#d32f2f'
                                        }}>
                                            {step.number}
                                        </Text>
                                    </div>
                                    <Title level={4} style={{ marginBottom: '12px', fontSize: '18px' }}>
                                        {step.title}
                                    </Title>
                                    <Paragraph style={{ color: '#666', whiteSpace: 'pre-line', fontSize: '14px' }}>
                                        {step.description}
                                    </Paragraph>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* Seconda riga con 2 step centrati */}
                    <Row gutter={[32, 32]} justify="center">
                        {steps.slice(3, 5).map((step, index) => (
                            <Col key={index + 3} xs={24} sm={12} lg={8}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        background: 'white',
                                        border: '4px solid #d32f2f',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 24px',
                                        boxShadow: '0 4px 20px rgba(211,47,47,0.2)'
                                    }}>
                                        <Text style={{
                                            fontSize: '36px',
                                            fontWeight: 'bold',
                                            color: '#d32f2f'
                                        }}>
                                            {step.number}
                                        </Text>
                                    </div>
                                    <Title level={4} style={{ marginBottom: '12px', fontSize: '18px' }}>
                                        {step.title}
                                    </Title>
                                    <Paragraph style={{ color: '#666', whiteSpace: 'pre-line', fontSize: '14px' }}>
                                        {step.description}
                                    </Paragraph>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* FAQ Section */}
            <div style={{
                padding: isMobile ? '60px 20px' : '80px 40px',
                background: '#f8f9fa',
                maxWidth: '900px',
                margin: '0 auto'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                    <QuestionCircleOutlined style={{ fontSize: '48px', color: '#d32f2f', marginBottom: '16px' }} />
                    <Title level={2} style={{ fontSize: isMobile ? '28px' : '36px', marginBottom: '16px' }}>
                        Domande Frequenti
                    </Title>
                    <Paragraph style={{ fontSize: '16px', color: '#666' }}>
                        Tutto quello che devi sapere su Babbo Natale Segreto
                    </Paragraph>
                </div>

                <Collapse
                    accordion
                    ghost
                    style={{ background: 'transparent' }}
                    expandIconPosition="end"
                >
                    {faqs.map((faq, index) => (
                        <Panel
                            header={
                                <Text style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>
                                    {faq.question}
                                </Text>
                            }
                            key={index}
                            style={{
                                marginBottom: '16px',
                                background: 'white',
                                borderRadius: '12px',
                                border: '2px solid #e0e0e0',
                                padding: '8px 0'
                            }}
                        >
                            <Paragraph style={{ color: '#666', marginBottom: 0, fontSize: '15px', lineHeight: '1.6' }}>
                                {faq.answer}
                            </Paragraph>
                        </Panel>
                    ))}
                </Collapse>
            </div>

            {/* Final CTA - Migliorato con urgency */}
            <div style={{
                background: '#ffffff',
                padding: isMobile ? '60px 20px' : '80px 40px',
                textAlign: 'center',
                borderTop: '4px solid #d32f2f',
                borderBottom: '4px solid #d32f2f',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '10%',
                    transform: 'translateY(-50%)',
                    fontSize: '120px',
                    opacity: 0.05,
                    color: '#d32f2f'
                }}>
                    🎁
                </div>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    right: '10%',
                    transform: 'translateY(-50%)',
                    fontSize: '120px',
                    opacity: 0.05,
                    color: '#d32f2f'
                }}>
                    🎄
                </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <Title level={2} style={{ color: '#d32f2f', marginBottom: '16px', fontSize: isMobile ? '28px' : '40px' }}>
                        Pronto per organizzare il tuo Secret Santa?
                    </Title>
                    <Paragraph style={{ color: '#555', fontSize: '18px', marginBottom: '8px' }}>
                        🎯 Crea il tuo sorteggio in meno di 2 minuti
                    </Paragraph>
                    {/*<Paragraph style={{ color: '#888', fontSize: '16px', marginBottom: '32px' }}>
                        Gratis per sempre. Nessuna carta di credito richiesta.
                    </Paragraph>*/}
                    <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={() => loginWithRedirect()}
                        style={{
                            height: '64px',
                            fontSize: '20px',
                            fontWeight: 600,
                            borderRadius: '32px',
                            padding: '0 48px',
                            background: '#2e7d32',
                            color: 'white',
                            border: '3px solid #1b5e20',
                            boxShadow: '0 8px 24px rgba(46,125,50,0.4)',
                            minWidth: isMobile ? '280px' : 'auto'
                        }}
                    >
                        Accedi e Inizia Ora
                    </Button>
                </div>
            </div>

            {/* Footer */}
            <div style={{
                padding: '40px 24px',
                textAlign: 'center',
                background: '#f8f9fa',
                borderTop: '2px solid #d32f2f'
            }}>
                <Space size="large" direction={isMobile ? 'vertical' : 'horizontal'} style={{ marginBottom: '16px' }}>
                    <a href="/privacy" style={{ color: '#666', textDecoration: 'none' }}>
                        Privacy Policy
                    </a>
                    <a href="/terms" style={{ color: '#666', textDecoration: 'none' }}>
                        Termini di Servizio
                    </a>
                    <a href="/cookies" style={{ color: '#666', textDecoration: 'none' }}>
                        Cookie Policy
                    </a>
                </Space>
                <Divider style={{ margin: '16px 0' }} />
                <Paragraph style={{ color: '#999', marginBottom: '8px', fontSize: '13px' }}>
                    © 2025 babbonatalesegreto.icu - Rendi speciale ogni scambio di regali
                </Paragraph>
                <Paragraph style={{ color: '#aaa', marginBottom: '8px', fontSize: '12px' }}>
                    Questo sito utilizza cookie tecnici necessari per il funzionamento del servizio di autenticazione.
                    <br />
                    I tuoi dati personali vengono trattati in conformità al GDPR (Regolamento UE 2016/679).
                </Paragraph>
                <Paragraph style={{ color: '#bbb', marginBottom: 0, fontSize: '11px' }}>
                    Powered by{' '}
                    <a
                        href="https://marchiosoft.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#d32f2f',
                            textDecoration: 'none',
                            fontWeight: 500
                        }}
                    >
                        marchiosoft.com
                    </a>
                </Paragraph>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
            `}</style>
        </div>
    );
};

export default Landing;
